import { z } from 'zod'

import { auditShape } from '@/shared/details'

export * from './paths'

const status = z.enum(['pending', 'active', 'deactivated'])

export type OrganizationStatus = z.infer<typeof status>

const organizationSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    status,
    parentId: z.string().nullish(),
    parentName: z.string().nullish(),
    siteId: z.string(),
    siteName: z.string().nullish(),
    description: z.string().nullish(),
    htmlDescription: z.string().nullish(),
    contactHtml: z.string().nullish(),
    externalId: z.string().nullish(),
    custom1: z.string().nullish(),
    custom2: z.string().nullish(),
    custom3: z.string().nullish(),
    administrativeNotes: z.string().nullish(),
    isAssignableToUsers: z.boolean(),
    addressLine1: z.string().nullish(),
    addressLine2: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    postalCode: z.string().nullish(),
    country: z.string().nullish(),
    email: z.string().nullish(),
    phone: z.string().nullish(),
  })
  .extend(auditShape)

/** An organization as its details page and edit form show it. */
export type Organization = z.infer<typeof organizationSchema>

export function parseOrganization(body: unknown): Organization {
  return organizationSchema.parse(body)
}

const summarySchema = z.object({
  id: z.string(),
  name: z.string(),
  status,
  externalId: z.string().nullish(),
  isAssignableToUsers: z.boolean(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  country: z.string().nullish(),
})

/** An organization as a list shows it. */
export type OrganizationSummary = z.infer<typeof summarySchema>

export function parseOrganizationSummary(item: unknown): OrganizationSummary {
  return summarySchema.parse(item)
}

const treeNodeSchema = z.object({
  id: z.string(),
  name: z.string(),
  status,
  hasChildren: z.boolean(),
})

/** An organization as Manage Organization Tree shows it. */
export type OrganizationTreeNode = z.infer<typeof treeNodeSchema>

export function parseOrganizationTreeNode(item: unknown): OrganizationTreeNode {
  return treeNodeSchema.parse(item)
}

const siteMovePreviewSchema = z.object({
  siteId: z.string(),
  siteName: z.string(),
  parentId: z.string().nullish(),
  parentName: z.string().nullish(),
  organizationCount: z.number(),
  userCount: z.number(),
  webPageCount: z.number(),
  defaultOrganizationFallsBack: z.boolean(),
  blocks: z.array(
    z.object({
      code: z.string(),
      message: z.string(),
      count: z.number(),
      records: z.array(z.object({ id: z.string(), name: z.string() })),
    }),
  ),
})

/** What moving an organization to another site would take, and what stops it. */
export type SiteMovePreview = z.infer<typeof siteMovePreviewSchema>

export function parseSiteMovePreview(body: unknown): SiteMovePreview {
  return siteMovePreviewSchema.parse(body)
}

/** The message id of a status's label. */
export function statusLabelId(value: OrganizationStatus): string {
  return `siteManagement.organizations.status.${value}`
}

/** The base of Organization Details' slots: tabs, side-menu links, header actions and panels (`detailsSlots`). */
export const organizationDetailsSlot = 'site-management.organization-details'
